import { Skeleton } from "@/components/ui/skeleton.tsx";
import { User, UserRole } from "@/lib/api/user.ts";
import { useSignedMedia } from "@/lib/hooks/use-signed-media.ts";
import { formatDate } from "@/lib/utils.ts";
import { useNavigate } from "@tanstack/react-router";
import {
  CakeIcon,
  HeartIcon,
  PhoneIcon,
  PinIcon,
  UserIcon,
} from "lucide-react";
import { ReactNode } from "react";
import { RoleBadge } from "../users/role-badge";
import { ResearchBadge } from "@/components/users/research-badge.tsx";
import { getPrimaryRole } from "@/lib/utils/system-roles";
import {
  buildRedactedPseudoId,
  isFieldRedacted,
} from "@/lib/redacted-display";

const REDACTED = "[redacted]";

const renderRedactable = (
  field: string,
  rawValue: string | null | undefined,
  redactedFields: string[] | undefined,
): string =>
  isFieldRedacted(field, redactedFields, rawValue)
    ? REDACTED
    : (rawValue || "-");

interface UserInfoProps {
  user?: User;
  isLoading: boolean;
}

export function UserInfo({ user, isLoading }: UserInfoProps) {
  const navigate = useNavigate();

  if (isLoading || !user) {
    return <Skeleton className="mt-4 h-72 w-full bg-white" />;
  }

  return (
    <div className="space-y-4">
      <div
        className="mx-auto mt-4 max-w-md cursor-pointer overflow-hidden rounded-xl bg-white shadow-md md:max-w-2xl"
        onClick={() => {
          navigate({
            to: "/users/$userId",
            params: {
              userId: user.id,
            },
          });
        }}
      >
        <ProfileHeader user={user} />
        <ProfileDetails user={user} />
      </div>
    </div>
  );
}

interface ProfileHeaderProps {
  user: User;
}

const ProfileHeader = ({ user }: ProfileHeaderProps) => {
  const photoRedacted = isFieldRedacted(
    "profile_picture_url",
    user.redacted_fields,
    user.profile_picture_url,
  );
  const nameRedacted = isFieldRedacted(
    "name",
    user.redacted_fields,
    user.name,
  );
  const birthdayRedacted = isFieldRedacted(
    "birthday",
    user.redacted_fields,
    user.birthday,
  );
  const locationRedacted = isFieldRedacted(
    "location",
    user.redacted_fields,
    user.location,
  );

  const { url } = useSignedMedia(
    photoRedacted ? "" : (user.profile_picture_url ?? ""),
  );
  const isResearchParticipant =
    user.role === UserRole.MENTEE &&
    user.profile?.metadata?.researchConsent?.consent === true;

  const displayName = nameRedacted
    ? buildRedactedPseudoId(user.id)
    : (user.name ?? "");

  return (
    <>
      <div className="block p-4">
        <RoleBadge role={getPrimaryRole(user.systemRoles)} />
      </div>
      <div className="flex items-center pl-4 pt-4">
        {url && !photoRedacted ? (
          <img className="h-16 w-16 rounded-full" src={url} alt="Profile" />
        ) : (
          <div className="h-16 w-16 rounded-full bg-gray-200"></div>
        )}

        <div className="ml-4">
          <div className="flex items-center gap-1">
            <h2 className="text-xl font-bold">
              {displayName}
              {birthdayRedacted
                ? ""
                : user.birthday
                  ? `, ${new Date().getFullYear() - new Date(user.birthday).getFullYear()}`
                  : ""}
            </h2>
            <ResearchBadge isResearchParticipant={isResearchParticipant} />
          </div>
          <p className="text-sm text-gray-600">
            {locationRedacted ? REDACTED : (user.location ?? "")}
          </p>
          <p className="text-sm text-gray-400">
            Joined {formatDate(user.created_at)}
          </p>
        </div>
      </div>
    </>
  );
};

interface ProfileDetailsProps {
  user: User;
}

const ProfileDetails = ({ user }: ProfileDetailsProps) => {
  const userR = user.redacted_fields;
  const profileR = user.profile?.redacted_fields;

  return (
    <div className="p-4">
      <ProfileDetailItem
        icon={<CakeIcon size={22} />}
        label="Birthday"
        value={
          isFieldRedacted("birthday", userR, user.birthday)
            ? REDACTED
            : user.birthday
              ? formatDate(user.birthday)
              : "-"
        }
      />
      <ProfileDetailItem
        icon={<UserIcon size={22} />}
        label="Pronouns"
        value={renderRedactable("pronouns", user.pronouns, userR)}
      />
      <ProfileDetailItem
        icon={<PhoneIcon size={22} />}
        label="Phone"
        value={renderRedactable("phone", user.phone, userR)}
      />
      {user.role === UserRole.MENTEE && (
        <>
          <ProfileDetailItem
            icon={<PinIcon size={22} />}
            label="Emergency Address"
            value={renderRedactable("location", user.location, userR)}
          />
          <ProfileDetailItem
            icon={<UserIcon size={22} />}
            label="Guardian Name (If Applicable)"
            value={renderRedactable("guardian_name", user.guardian_name, userR)}
          />
          <ProfileDetailItem
            icon={<PhoneIcon size={22} />}
            label="Emergency Contact"
            value={renderRedactable(
              "emergency_phone",
              user.emergency_phone,
              userR,
            )}
          />

          <ProfileDetailItem
            icon={<HeartIcon size={22} />}
            label="Life Experiences"
            value={
              isFieldRedacted(
                "life_experiences",
                profileR,
                user.profile?.life_experiences,
              )
                ? REDACTED
                : user.profile?.life_experiences?.join(", ") || "-"
            }
          />
          <ProfileDetailItem
            icon={<HeartIcon size={22} />}
            label="Interests"
            value={
              isFieldRedacted("interests", profileR, user.profile?.interests)
                ? REDACTED
                : user.profile?.interests?.join(", ") || "-"
            }
          />
          <ProfileDetailItem
            icon={<HeartIcon size={22} />}
            label="Music Interests"
            value={renderRedactable(
              "favorite_music",
              user.profile?.favorite_music,
              profileR,
            )}
          />
        </>
      )}
    </div>
  );
};

interface ProfileDetailItemProps {
  icon: ReactNode;
  label: string;
  value: string;
}

const ProfileDetailItem = ({ icon, label, value }: ProfileDetailItemProps) => {
  return (
    <div className="mb-3 flex items-center">
      <span className="text-xl">{icon}</span>
      <div className="ml-4">
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-sm text-gray-600">{value}</p>
      </div>
    </div>
  );
};
